import api from "./api";

const orderService = {
  // Get all orders for admin
  getAllOrders: async (filters = {}) => {
    try {
      const params = new URLSearchParams();

      if (filters.status) params.append("status", filters.status);
      if (filters.payment_status)
        params.append("payment_status", filters.payment_status);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.user_id) params.append("user_id", filters.user_id);
      if (filters.page) params.append("page", filters.page);
      if (filters.limit) params.append("limit", filters.limit);

      // Try admin endpoint first, fallback to vendor endpoint
      try {
        const response = await api.get(
          `/api/admin/orders?${params.toString()}`
        );
        return response.data;
      } catch (adminError) {
        console.warn(
          "Admin orders endpoint not available, using vendor endpoint:",
          adminError
        );
        const response = await api.get(
          `/api/vendor/orders?${params.toString()}`
        );
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      throw error;
    }
  },

  // Get order by ID
  getOrderById: async (orderId) => {
    try {
      // Try admin endpoint first, fallback to customer endpoint
      try {
        const response = await api.get(`/api/admin/orders/${orderId}`);
        return response.data;
      } catch (adminError) {
        console.warn(
          "Admin order endpoint not available, using customer endpoint:",
          adminError
        );
        const response = await api.get(`/api/customer/orders/${orderId}`);
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      throw error;
    }
  },

  // Update order status
  updateOrderStatus: async (orderId, status, additionalData = {}) => {
    try {
      const requestData = {
        status: status,
        ...additionalData,
      };

      // Try admin endpoint first, fallback to vendor endpoint
      try {
        const response = await api.patch(
          `/api/admin/orders/${orderId}/status`,
          requestData
        );
        return response.data;
      } catch (adminError) {
        console.warn(
          "Admin order status endpoint not available, using vendor endpoint:",
          adminError
        );
        const response = await api.patch(
          `/api/vendor/orders/${orderId}/status`,
          requestData
        );
        return response.data;
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      throw error;
    }
  },

  // Cancel order
  cancelOrder: async (orderId, additionalData = {}) => {
    try {
      const requestData = {
        status: "cancelled",
        ...additionalData,
      };

      // Try admin endpoint first, fallback to customer endpoint
      try {
        const response = await api.patch(
          `/api/admin/orders/${orderId}/status`,
          requestData
        );
        return response.data;
      } catch (adminError) {
        console.warn(
          "Admin cancel order endpoint not available, using customer endpoint:",
          adminError
        );
        const response = await api.patch(
          `/api/customer/orders/${orderId}/cancel`,
          requestData
        );
        return response.data;
      }
    } catch (error) {
      console.error("Error cancelling order:", error);
      throw error;
    }
  },

  // Get order statistics
  getOrderStats: async () => {
    try {
      const response = await api.get("/api/admin/dashboard/stats");
      return response.data;
    } catch (error) {
      console.error("Error fetching order stats:", error);
      throw error;
    }
  },

  // Get order timeline
  getOrderTimeline: async (orderId) => {
    try {
      const response = await api.get(`/api/admin/orders/${orderId}/timeline`);
      return response.data;
    } catch (error) {
      console.error("Error fetching order timeline:", error);
      throw error;
    }
  },

  // Book 3PL Shipment
  bookShipment: async (orderId, courierPartner) => {
    try {
      const response = await api.post(`/api/admin/orders/${orderId}/book-shipment`, { courier_partner: courierPartner });
      return response.data;
    } catch (error) {
      console.error("Error booking shipment:", error);
      throw error;
    }
  },

  // Get Shadowfax real-time tracking details
  getShadowfaxTracking: async (awb) => {
    try {
      const response = await api.get(`/api/shadowfax/shipment/track/${awb}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Shadowfax tracking details:", error);
      throw error;
    }
  },

  // Get Shadowfax real-time reverse tracking details
  getShadowfaxReverseTracking: async (awb) => {
    try {
      const response = await api.get(`/api/shadowfax/pickup/track/${awb}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Shadowfax reverse tracking details:", error);
      throw error;
    }
  },

  // Check serviceability for delivery pincode
  checkShadowfaxServiceability: async (pincode) => {
    try {
      const response = await api.post(`/api/shadowfax/serviceability`, { pincode });
      return response.data;
    } catch (error) {
      console.error("Error checking Shadowfax serviceability:", error);
      throw error;
    }
  },

  // Check serviceability by service name (e.g. customer_delivery, customer_pickup)
  checkShadowfaxServiceabilityByServiceName: async (pincode, serviceName = "customer_delivery") => {
    try {
      const response = await api.post(`/api/shadowfax/serviceability-by-service`, { pincode, service_name: serviceName });
      console.log("serviceability-by-service response", response.data);
      return response.data;
    } catch (error) {
      console.error("Error checking Shadowfax serviceability by service name:", error);
      throw error;
    }
  },

  // Check order serviceability (Customer Delivery + All Vendor Pickups)
  checkOrderServiceability: async (orderId) => {
    try {
      const response = await api.post(`/api/shadowfax/order-serviceability`, { order_id: orderId });
      console.log("order serviceability response", response.data);
      return response.data;
    } catch (error) {
      console.error("Error checking order serviceability:", error);
      throw error;
    }
  },

  // Check serviceability for return/pickup pincode
  checkShadowfaxPickupServiceability: async (pincode) => {
    try {
      const response = await api.post(`/api/shadowfax/pickup/serviceability`, { pincode });
      return response.data;
    } catch (error) {
      console.error("Error checking Shadowfax pickup serviceability:", error);
      throw error;
    }
  },

  // Cancel booked Shadowfax shipment
  cancelShadowfaxShipment: async (awb, reason) => {
    try {
      const response = await api.post(`/api/shadowfax/shipment/cancel`, { awb_number: awb, reason });
      return response.data;
    } catch (error) {
      console.error("Error cancelling Shadowfax shipment:", error);
      throw error;
    }
  },

  // Get XpressBees real-time tracking details
  getXpressbeesTracking: async (awb) => {
    try {
      const response = await api.get(`/api/xpressbees/shipment/track/${awb}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching XpressBees tracking details:", error);
      throw error;
    }
  },

  // Check serviceability for delivery pincode via XpressBees
  checkXpressbeesServiceability: async (pincode) => {
    try {
      const response = await api.post(`/api/xpressbees/serviceability`, { origin: "302020", destination: pincode });
      return response.data;
    } catch (error) {
      console.error("Error checking XpressBees serviceability:", error);
      throw error;
    }
  },

  // Check order serviceability via XpressBees (Customer Delivery + All Vendor Pickups)
  checkXpressbeesOrderServiceability: async (orderId) => {
    try {
      const response = await api.post(`/api/xpressbees/order-serviceability`, { order_id: orderId });
      console.log("xpressbees order serviceability response", response.data);
      return response.data;
    } catch (error) {
      console.error("Error checking XpressBees order serviceability:", error);
      throw error;
    }
  },

  // Schedule reverse pickup via XpressBees
  createXpressbeesReversePickup: async (refundId) => {
    try {
      const response = await api.post(`/api/xpressbees/pickup/create`, { refund_id: refundId });
      return response.data;
    } catch (error) {
      console.error("Error creating XpressBees reverse pickup:", error);
      throw error;
    }
  },

  // Get XpressBees reverse tracking details
  getXpressbeesReverseTracking: async (awb) => {
    try {
      const response = await api.get(`/api/xpressbees/pickup/track/${awb}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching XpressBees reverse tracking details:", error);
      throw error;
    }
  },

  // Cancel XpressBees shipment
  cancelXpressbeesShipment: async (awb, reason) => {
    try {
      const response = await api.post(`/api/xpressbees/shipment/cancel`, { awb_number: awb, reason });
      return response.data;
    } catch (error) {
      console.error("Error cancelling XpressBees shipment:", error);
      throw error;
    }
  },

  // Get Delhivery real-time tracking details
  getDelhiveryTracking: async (awb) => {
    try {
      const response = await api.get(`/api/delhivery/shipment/track/${awb}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Delhivery tracking details:", error);
      throw error;
    }
  },

  // Check serviceability for delivery pincode via Delhivery
  checkDelhiveryServiceability: async (pincode) => {
    try {
      const response = await api.post(`/api/delhivery/serviceability`, { pincode });
      return response.data;
    } catch (error) {
      console.error("Error checking Delhivery serviceability:", error);
      throw error;
    }
  },

  // Check order serviceability via Delhivery
  checkDelhiveryOrderServiceability: async (orderId) => {
    try {
      const response = await api.post(`/api/delhivery/order-serviceability`, { order_id: orderId });
      return response.data;
    } catch (error) {
      console.error("Error checking Delhivery order serviceability:", error);
      throw error;
    }
  },

  // Schedule reverse pickup via Delhivery
  createDelhiveryReversePickup: async (refundId) => {
    try {
      const response = await api.post(`/api/delhivery/pickup/create`, { refund_id: refundId });
      return response.data;
    } catch (error) {
      console.error("Error creating Delhivery reverse pickup:", error);
      throw error;
    }
  },

  // Get Delhivery reverse tracking details
  getDelhiveryReverseTracking: async (awb) => {
    try {
      const response = await api.get(`/api/delhivery/pickup/track/${awb}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Delhivery reverse tracking details:", error);
      throw error;
    }
  },

  // Cancel Delhivery shipment
  cancelDelhiveryShipment: async (awb, reason) => {
    try {
      const response = await api.post(`/api/delhivery/shipment/cancel`, { awb_number: awb, reason });
      return response.data;
    } catch (error) {
      console.error("Error cancelling Delhivery shipment:", error);
      throw error;
    }
  },

  // Get Delhivery packing slip / label
  getDelhiveryPackingSlip: async (awb) => {
    try {
      const response = await api.get(`/api/delhivery/shipment/label/${awb}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching Delhivery packing slip:", error);
      throw error;
    }
  },
};

export default orderService;
